// DesignationPicker — canonical `designation_picker` field primitive. Promoted
// off the CHG-1167 _FieldStub (DRIFT-910 slice 2, CHG-2462). Returns the chosen
// Designation.id (string) or null.
//
// v2 implementation: a self-fetching wrapper over the a11y-complete SelectField,
// sourced from the flat designationsApi.listDesignations list. The v1 reference
// (web/dashboard/src/components/forms/DesignationPicker.tsx) is a TREE picker on
// designationsApi.tree() with lucide/Tailwind chrome; the v2 client exposes only
// the flat list endpoint and the v2 design system is CSS-var + FieldShell-based,
// so this slice ships the flat-select form rather than porting the v1 tree
// verbatim (per the brief's "do not copy verbatim" guidance). A richer
// tree / inline-create picker can follow if a use case needs it. Options are
// unfiltered by status (admin link context — draft/active designations are valid
// link targets); name-sorted for scannability.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('designation_picker').
// Doctrine: feedback_field_primitive_components_canonical.md. a11y is inherited
// 1:1 from SelectField (label htmlFor, aria-invalid/required, aria-describedby).

import { useQuery } from "@tanstack/react-query";
import type { FieldPrimitiveProps } from "./types";
import { SelectField, type SelectOption } from "./SelectField";
import { designationsApi } from "../../api/designations";

export function DesignationPicker(props: FieldPrimitiveProps<string>) {
  const { data, isLoading } = useQuery({
    queryKey: ["designations", "picker-options"],
    queryFn: () =>
      designationsApi.listDesignations({
        pageSize: 200,
        sortField: "name",
        sortDirection: "asc",
      }),
  });
  const options: SelectOption[] = (data?.items ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));
  return (
    <SelectField
      {...props}
      options={options}
      placeholder={isLoading ? "Loading designations…" : "Select designation…"}
      disabled={props.disabled || isLoading}
    />
  );
}
